import type { SearchDataModel } from "~/types/models/SearchData.model";
import type { IArticle, IAuthor } from "~/types";
import type { IListResponse } from "~/types/IListResponse";

export const useProxyService = () => {
  const getArticles = async (
    researchTopicId: number,
    params: SearchDataModel,
    options: { getFromCache: boolean } = { getFromCache: false }
  ): Promise<{ error?: string; articles: IArticle[] }> => {
    try {
      const { data } = useNuxtData("articles");

      if (options.getFromCache && data.value) {
        return data.value;
      }

      if (import.meta.server) {
        const { getArticles } = useMongoDbService();
        const articles = await getArticles(researchTopicId, params);
        return { articles };
      } else {
        const { getArticlesByResearchTopicId } = useRtApiService();
        const articles = await getArticlesByResearchTopicId(
          researchTopicId,
          params
        );
        return { articles };
      }
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : "Unknown error",
        articles: []
      };
    }
  };

  const getAuthors = async (
    researchTopicId: number,
    params: SearchDataModel,
    options: { getFromCache: boolean } = { getFromCache: false }
  ): Promise<{ error?: string; authors: IListResponse<IAuthor> }> => {
    try {
      const { data } = useNuxtData("authors");

      if (options.getFromCache && data.value) {
        return data.value;
      }

      if (import.meta.server) {
        const { getAuthors } = useMongoDbService();
        const authors = await getAuthors(researchTopicId, params);
        return { authors };
      } else {
        const { getAuthorsByResearchTopicId } = useRtApiService();
        const authors = await getAuthorsByResearchTopicId(
          researchTopicId,
          params
        );
        return { authors };
      }
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : "Unknown error",
        authors: {
          total: 0,
          offset: 0,
          limit: 0,
          items: []
        }
      };
    }
  };

  return { getArticles, getAuthors };
};
